import { useState } from "react";
import { copyText } from "@/lib/copy";
import { useLibrary, toggleSnippet } from "@/lib/library";
import type { UseCaseExample } from "@/data/use-cases";

/**
 * In-context example snippets for a use-case page. Each card shows a ready-to-use
 * line (e.g. "★★★★☆ (4/5)") with a label; clicking the card copies the whole
 * snippet, and the ★ button saves it to the Saved snippets list (/my-symbols).
 */
export function UseCaseExamples({ examples }: { examples: UseCaseExample[] }) {
  const { savedSnippets } = useLibrary();
  const [copied, setCopied] = useState<number | null>(null);

  async function handleCopy(value: string, i: number) {
    const ok = await copyText(value, { source: "use-case-example" });
    if (ok) {
      setCopied(i);
      window.setTimeout(() => setCopied((c) => (c === i ? null : c)), 1200);
    }
  }

  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {examples.map((ex, i) => {
        const saved = savedSnippets.includes(ex.text);
        const plain = ex.text.replace(/\n/g, " ");
        return (
          <li key={i} className="relative">
            <button
              type="button"
              onClick={() => handleCopy(ex.text, i)}
              aria-label={`Copy example: ${plain}`}
              className="glass group flex w-full flex-col gap-2 rounded-xl px-4 py-4 pr-11 text-left transition-colors hover:border-primary/40"
            >
              <span className="whitespace-pre-wrap break-words font-sans text-lg leading-snug text-foreground">
                {ex.text}
              </span>
              <span className="flex items-center gap-2 text-xs">
                <span className={copied === i ? "text-accent" : "text-muted-foreground"}>
                  {copied === i ? "Copied!" : ex.label}
                </span>
                {copied !== i && (
                  <span className="ml-auto whitespace-nowrap text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
                    Click to copy
                  </span>
                )}
              </span>
            </button>
            <button
              type="button"
              onClick={() => toggleSnippet(ex.text)}
              aria-pressed={saved}
              aria-label={saved ? `Remove "${plain}" from saved snippets` : `Save "${plain}" to snippets`}
              title={saved ? "Saved — click to remove" : "Save to snippets"}
              className={
                "absolute right-2 top-2 rounded-md px-1.5 py-0.5 text-lg leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                (saved ? "text-amber-400" : "text-muted-foreground hover:text-foreground")
              }
            >
              {saved ? "★" : "☆"}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
